/**
 * Cold-connect retry ladder.
 *
 * Owns the retry timer for a NATS connection that could not be established
 * (mint failed, WebSocket rejected, handshake watchdog fired, broker refused
 * the JWT, recovery rebuild failed). The connection-promise singleton clears
 * its slot on rejection; nothing else schedules the next attempt, so this
 * module does.
 *
 * Properties:
 *   - Exponential rungs {1, 2, 4, 8, 16, 30} s with full jitter, then the
 *     30 s cap FOREVER. There is no last rung: a session that cannot reach
 *     the broker keeps trying, cheaply, until it can, until the identity
 *     flips, or until the tab closes.
 *   - Single-flight: a second `armColdConnectRetry()` while a timer is
 *     pending is a no-op.
 *   - Owned: the pin records the `owner` token of the effect that armed it.
 *     `cancelColdConnectRetry({ owner })` is a no-op for a foreign owner,
 *     so an unmounting effect cannot kill a newer effect's rung, while
 *     `preReplaceCleanup` cancels unconditionally before an identity flip.
 *   - Supersedable: a `performRetry` that discovers its effect is gone
 *     throws `ColdConnectRetrySuperseded`, which stops the ladder without
 *     re-arming.
 *   - Edge-driven: `retryColdConnectNow()` collapses the pending delay
 *     (online / visible edges), `deferColdConnectRetry()` parks it (app
 *     backgrounded) until the next `retryColdConnectNow()`.
 *
 * Pinned on `globalThis.__morfi_coldRetry` (HMR-safe). Every failed rung is
 * reported through `onAttemptFailed` (→ `reportNatsFailure`); success emits
 * one recovery event carrying attempts + downtime.
 */

import { reportNatsRecovery } from './report-nats-failure.js';

/**
 * Exponential ladder · base delays in ms · {1s, 2s, 4s, 8s, 16s, 30s}.
 * Subject to full-jitter (multiplied by `Math.random()`) per AWS-style
 * jitter recommendations — prevents thundering-herd reconnects.
 */
export const COLD_CONNECT_RETRY_DELAYS_MS: readonly number[] = [
  1_000, 2_000, 4_000, 8_000, 16_000, 30_000,
];

/** Every rung past the ladder's end uses this base delay. Never terminal. */
export const COLD_CONNECT_CAP_MS = 30_000;

const RETRY_PIN_KEY = '__morfi_coldRetry' as const;

declare global {
  var __morfi_coldRetry: ColdConnectRetryState | undefined;
}

export interface ColdConnectRetryState {
  /** Attempt number (1-based). Starts at 1 on first arm. */
  attempt: number;
  /** setTimeout handle — `unknown` since Node vs DOM differ; `null` while deferred. */
  timer: unknown;
  /** Wall-clock ms at which the next retry is scheduled (`0` while deferred). */
  nextAt: number;
  /** Reason the retry was armed. */
  reason: ColdConnectRetryReason;
  /** Token of the effect that armed the ladder. */
  owner: number;
  /** Wall-clock ms of the first arm — recovery reports the downtime from here. */
  armedAt: number;
  /** Parked by `deferColdConnectRetry` (app backgrounded) until the next edge. */
  deferred: boolean;
  performRetry: () => Promise<void> | void;
  opts: ColdConnectRetryOpts;
}

export type ColdConnectRetryReason =
  | 'wsconnect-reject'
  | 'tls-error'
  | 'dns-error'
  | 'edge-503'
  | 'unknown-transport'
  // NATS server rejected the credentials at the protocol handshake
  // (expired / empty / clock-skewed JWT → `Authorization Violation`).
  // The retry re-mints a fresh JWT before reconnecting.
  | 'auth-rejected'
  // The socket opened but the broker never sent INFO (watchdog).
  | 'handshake-timeout'
  // The backend refused the credential mint (4xx) — start at the cap.
  | 'mint-rejected'
  // The credential mint exhausted its 5xx / network ladder.
  | 'mint-failed';

/** Thrown by `performRetry` when its owning effect is gone: stop, no re-arm. */
export class ColdConnectRetrySuperseded extends Error {
  constructor() {
    super('cold-connect retry superseded');
    this.name = 'ColdConnectRetrySuperseded';
  }
}

export interface ColdConnectAttemptFailure {
  readonly attempt: number;
  readonly reason: ColdConnectRetryReason;
  readonly error: unknown;
  readonly nextRetryMs: number;
}

type GlobalWithRetry = typeof globalThis & {
  [RETRY_PIN_KEY]?: ColdConnectRetryState | undefined;
};

export interface ColdConnectRetryOpts {
  readonly setTimeoutFn?: (h: () => void, ms: number) => unknown;
  readonly clearTimeoutFn?: (h: unknown) => void;
  readonly randomFn?: () => number;
  readonly now?: () => number;
  /** Fired after every failed rung with the delay already scheduled. */
  readonly onAttemptFailed?: (failure: ColdConnectAttemptFailure) => void;
}

function getSetTimeout(
  fn: ((h: () => void, ms: number) => unknown) | undefined,
): (h: () => void, ms: number) => unknown {
  return (
    fn ??
    ((h: () => void, ms: number): unknown =>
      (globalThis as { setTimeout: typeof setTimeout }).setTimeout(h, ms))
  );
}

function getClearTimeout(fn: ((h: unknown) => void) | undefined): (h: unknown) => void {
  return (
    fn ??
    ((h: unknown): void =>
      (globalThis as { clearTimeout: typeof clearTimeout }).clearTimeout(
        h as ReturnType<typeof setTimeout>,
      ))
  );
}

function getNow(fn: (() => number) | undefined): () => number {
  return fn ?? ((): number => Date.now());
}

/**
 * Compute the full-jitter delay for the given attempt. Returns a value
 * in `[0, base)` so the spread of reconnects across attempt N is
 * uniform inside the base window. Attempts past the ladder use the cap.
 */
export function computeColdConnectDelayMs(attempt: number, randomFn: () => number): number {
  const idx = Math.min(attempt - 1, COLD_CONNECT_RETRY_DELAYS_MS.length - 1);
  const base = COLD_CONNECT_RETRY_DELAYS_MS[idx] ?? COLD_CONNECT_CAP_MS;
  return Math.floor(randomFn() * base);
}

/** First rung for a reason: a refused mint starts at the cap, everything else at 1 s. */
function firstAttemptFor(reason: ColdConnectRetryReason): number {
  return reason === 'mint-rejected' ? COLD_CONNECT_RETRY_DELAYS_MS.length : 1;
}

/**
 * Arm the retry timer. The supplied `performRetry` callback runs on
 * timer expiry — typically the full re-mint + reconnect bootstrap.
 *
 * Single-flight: if a timer is already pending, the call is a no-op and
 * returns `null`. Otherwise returns the delay (ms) of the first rung.
 */
export function armColdConnectRetry(args: {
  readonly reason: ColdConnectRetryReason;
  readonly performRetry: () => Promise<void> | void;
  readonly owner?: number;
  readonly opts?: ColdConnectRetryOpts;
}): number | null {
  const g = globalThis as GlobalWithRetry;
  if (g[RETRY_PIN_KEY] !== undefined) return null;

  const opts = args.opts ?? {};
  const now = getNow(opts.now);
  const randomFn = opts.randomFn ?? Math.random;
  const attempt = firstAttemptFor(args.reason);
  const delayMs = computeColdConnectDelayMs(attempt, randomFn);
  const state: ColdConnectRetryState = {
    attempt,
    timer: null,
    nextAt: 0,
    reason: args.reason,
    owner: args.owner ?? 0,
    armedAt: now(),
    deferred: false,
    performRetry: args.performRetry,
    opts,
  };
  g[RETRY_PIN_KEY] = state;
  schedule(state, delayMs);
  return delayMs;
}

function schedule(state: ColdConnectRetryState, delayMs: number): void {
  const setT = getSetTimeout(state.opts.setTimeoutFn);
  const now = getNow(state.opts.now);
  state.deferred = false;
  state.nextAt = now() + delayMs;
  state.timer = setT(() => {
    void runRetry(state);
  }, delayMs);
}

async function runRetry(state: ColdConnectRetryState): Promise<void> {
  const g = globalThis as GlobalWithRetry;
  if (g[RETRY_PIN_KEY] !== state) return;
  // Clear the pin BEFORE invoking the retry so a synchronous
  // re-arm (from a nested transport failure) can claim the slot.
  g[RETRY_PIN_KEY] = undefined;
  state.timer = null;

  try {
    await state.performRetry();
  } catch (cause) {
    if (cause instanceof ColdConnectRetrySuperseded) return;
    advanceLadder(state, cause);
    return;
  }
  reportNatsRecovery({
    stage: 'ws-connect',
    code: 'NATS_CONNECT_RECOVERED',
    attempts: state.attempt,
    downtimeMs: Math.max(0, getNow(state.opts.now)() - state.armedAt),
  });
}

function advanceLadder(state: ColdConnectRetryState, lastError: unknown): void {
  const g = globalThis as GlobalWithRetry;
  // A newer owner armed its own ladder while this rung was running (the
  // identity flip cancels + re-arms); leave theirs alone.
  if (g[RETRY_PIN_KEY] !== undefined) return;
  const randomFn = state.opts.randomFn ?? Math.random;
  const next: ColdConnectRetryState = {
    ...state,
    attempt: state.attempt + 1,
    timer: null,
  };
  const delayMs = computeColdConnectDelayMs(next.attempt, randomFn);
  g[RETRY_PIN_KEY] = next;
  schedule(next, delayMs);
  state.opts.onAttemptFailed?.({
    attempt: state.attempt,
    reason: state.reason,
    error: lastError,
    nextRetryMs: delayMs,
  });
}

/**
 * Cancel any pending retry. With `owner`, only a pin armed by that owner is
 * cancelled (an unmounting effect must not kill a newer effect's ladder);
 * without it the cancel is unconditional (identity flip, logout).
 * Idempotent.
 */
export function cancelColdConnectRetry(
  opts: ColdConnectRetryOpts & { readonly owner?: number } = {},
): void {
  const g = globalThis as GlobalWithRetry;
  const current = g[RETRY_PIN_KEY];
  if (current === undefined) return;
  if (opts.owner !== undefined && current.owner !== opts.owner) return;
  const clearT = getClearTimeout(opts.clearTimeoutFn ?? current.opts.clearTimeoutFn);
  if (current.timer !== null) clearT(current.timer);
  g[RETRY_PIN_KEY] = undefined;
}

/** Alias preserved for the original acceptance criteria. */
export const cancel = cancelColdConnectRetry;

/**
 * Collapse the pending delay and retry immediately (network came back, app
 * became visible, bfcache restore). No-op when nothing is armed.
 */
export function retryColdConnectNow(): void {
  const g = globalThis as GlobalWithRetry;
  const current = g[RETRY_PIN_KEY];
  if (current === undefined) return;
  const clearT = getClearTimeout(current.opts.clearTimeoutFn);
  if (current.timer !== null) clearT(current.timer);
  current.timer = null;
  void runRetry(current);
}

/**
 * Park the pending rung (app backgrounded on RN): the timer is cleared but
 * the pin stays, so the next `retryColdConnectNow()` resumes the ladder.
 */
export function deferColdConnectRetry(): void {
  const g = globalThis as GlobalWithRetry;
  const current = g[RETRY_PIN_KEY];
  if (current === undefined || current.deferred) return;
  const clearT = getClearTimeout(current.opts.clearTimeoutFn);
  if (current.timer !== null) clearT(current.timer);
  current.timer = null;
  current.nextAt = 0;
  current.deferred = true;
}

/** Read-only state accessor — exposed for tests + telemetry. */
export function getColdConnectRetryState(): ColdConnectRetryState | undefined {
  return (globalThis as GlobalWithRetry)[RETRY_PIN_KEY];
}

/** `true` while a retry is pending (scheduled or deferred). */
export function isColdConnectRetryArmed(): boolean {
  return (globalThis as GlobalWithRetry)[RETRY_PIN_KEY] !== undefined;
}

/** Test-only · clear the pinned state. Production must NOT call this. */
export function resetColdConnectRetry(): void {
  const g = globalThis as GlobalWithRetry;
  const current = g[RETRY_PIN_KEY];
  if (current !== undefined && current.timer !== null) {
    try {
      const clearT = getClearTimeout(undefined);
      clearT(current.timer);
    } catch {
      // Tolerate clearTimeout throws on dead handles.
    }
  }
  g[RETRY_PIN_KEY] = undefined;
}
